/**
 * The desktop app's bridge (docs/desktop-bridge.md).
 *
 * Only there inside Mauvely Assist for desktop, or a Mauvely app's panel
 * (`/panel`), which add `MauvelyAssist/` to the user agent and put
 * `window.assistDesktop` on the page soon after it loads. On the web every
 * call here answers "nothing" at once and never waits.
 *
 * The page asks the machine to run a call; it cannot allow one. What needs
 * the person's say is asked by the app itself, in a sheet the page cannot
 * draw or answer.
 */

import { DEVICE_ID, DEVICE_PLATFORMS, MAX_DEVICE_NAME_CHARS, MAX_HOST_CHARS, MAX_RESULT_IMAGES, type DevicePlatform } from '@wire';
import { uploadFile, type ClientTool, type HostContext, type HubServerFrame, type PendingCall } from './api';

interface Signal<A extends unknown[]> {
  connect(fn: (...args: A) => void): void;
  disconnect(fn: (...args: A) => void): void;
}

/** The QWebChannel object: an invokable's answer comes to a callback. */
interface Channel {
  version: string;
  tools(cb: (json: string) => void): void;
  servers(cb: (json: string) => void): void;
  run(callId: string, name: string, args: string): void;
  finished: Signal<[callId: string, content: string, isError: boolean, images?: string]>;
  toolsChanged: Signal<[]>;
  openSettings(section: 'folders' | 'servers' | 'computer'): void;
  /** Not in apps from before 2026-09-24. */
  setTheme?(theme: 'light' | 'dark'): void;
  /** Assist's own window only, from 2026-09-28: computer use. */
  replyEnded?(messageId: string): void;
  /** The same window, from 2026-10-09: the chat a reply is in, so the
   *  windows granted to it are the chat's. Never for a reply from a wake. */
  replyChat?(messageId: string, chatId: string): void;
  /** The same window: the person stopped the chat's reply, so its windows go. */
  chatStopped?(chatId: string): void;
  /** Assist's own window only, from 2026-10-09: this PC, for Dispatch. */
  device?(cb: (json: string) => void): void;
  /** The same window: a sheet asking about a call opened or was answered. */
  asking?: Signal<[callId: string, on: boolean]>;
  /** The same window: a reply was taken on from the hub's `wake`. */
  dispatched?(messageId: string): void;
  /** The same window: a sheet a paired phone may answer opened, as JSON
   *  `ApprovalRequest`. */
  approvalRequest?: Signal<[json: string]>;
  /** The same window: a paired phone's answer, for the shell to check. */
  approve?(json: string, cb: (answer: string) => void): void;
  /** The same window: a phone asking to be paired, for the shell to check. */
  pair?(json: string, cb: (answer: string) => void): void;
}

declare global {
  interface Window {
    assistDesktop?: Channel;
  }
}

/** Tells the desktop app the page's theme, so its title bar matches. */
export async function tellDesktopTheme(theme: 'light' | 'dark'): Promise<void> {
  (await channel())?.setTheme?.(theme);
}

/** Tells the desktop app a reply is over: its sheets close, and the windows
 *  it was granted stay with its chat, idle, or are let go when the app does
 *  not know its chat. */
export async function tellDesktopReplyEnded(messageId: string): Promise<void> {
  if (!inDesktop()) return;
  (await channel())?.replyEnded?.(messageId);
}

/** Tells the desktop app the person stopped a chat's reply, here or from
 *  anywhere the hub's `cancel` came: the windows the chat holds are let go. */
export async function tellDesktopChatStopped(chatId: string): Promise<void> {
  if (!inDesktop()) return;
  (await channel())?.chatStopped?.(chatId);
}

/** Replies this page took on from the hub's `wake`. Their calls never join
 *  the chat's windows: the app is told `dispatched` before each batch, in
 *  place of `replyChat`, whoever sent the message. */
const fromWake = new Set<string>();

/** The reply `messageId` is being taken on from the hub's `wake`. Said
 *  before it is taken on, so none of its calls can reach the app first. */
export function noteFromWake(messageId: string): void {
  fromWake.add(messageId);
}

/** Tells the desktop app a reply was taken on from the hub's `wake`, so it
 *  says the PC is working on a task from the phone. */
export async function tellDesktopDispatched(messageId: string): Promise<void> {
  if (!inDesktop()) return;
  (await channel())?.dispatched?.(messageId);
}

/** This PC, as Assist's own window names it: `device()`. */
export interface DeviceInfo {
  id: string;
  name: string;
  platform: DevicePlatform;
}

/** `device()`'s answer, or null for an app that does not run Dispatch (`""`)
 *  or one whose id or platform the Worker would refuse. A long name is cut to
 *  what the Worker keeps. */
export function parseDevice(json: string): DeviceInfo | null {
  const v = json ? parsed<Partial<Record<keyof DeviceInfo, unknown>> | null>(json, null) : null;
  if (!v || typeof v.id !== 'string' || !DEVICE_ID.test(v.id)) return null;
  if (typeof v.platform !== 'string' || !DEVICE_PLATFORMS.includes(v.platform as DevicePlatform)) return null;
  const name = typeof v.name === 'string' ? [...v.name.replace(/\p{Cc}/gu, ' ').replace(/\s+/g, ' ').trim()].slice(0, MAX_DEVICE_NAME_CHARS).join('').trim() : '';
  return name ? { id: v.id, name, platform: v.platform as DevicePlatform } : null;
}

/** This page's PC, once `desktopDevice` has asked. */
let ownId: string | null = null;
export const ownDeviceId = (): string | null => ownId;

/** Whether a page that is the PC `own` may run the calls in a chat: any
 *  chat but another PC's thread, whose calls only that PC answers (403). */
export const ownsThread = (deviceId: string | null | undefined, own: string | null): boolean => !deviceId || deviceId === own;

/** The id the shell runs a call under, `<messageId>:<callId>`, taken apart.
 *  Message ids have no colon. */
export function splitRunId(runId: string): { messageId: string; callId: string } | null {
  const at = runId.indexOf(':');
  return at > 0 && at < runId.length - 1 ? { messageId: runId.slice(0, at), callId: runId.slice(at + 1) } : null;
}

/** A local MCP server, as the Connectors tab lists it. */
export interface DesktopServer {
  id: string;
  name: string;
  state: 'running' | 'starting' | 'failed' | 'off';
  tools: number;
}

/** What came out of one call, as `continue` takes it. */
export interface DesktopResult {
  callId: string;
  content: string;
  isError?: boolean;
  /** The images it gave back, uploaded to the chat. */
  files?: string[];
}

/** An image a call gave back. */
export interface ResultImage {
  mime: string;
  bytes: Uint8Array<ArrayBuffer>;
}

const MAX_RESULT_IMAGE_BYTES = 2 * 1024 * 1024;
const RESULT_EXTENSIONS: Record<string, string> = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp', 'image/gif': 'gif' };

/** Inside the desktop app, whether or not the bridge has arrived yet. */
export const inDesktop = (): boolean =>
  typeof navigator !== 'undefined' && navigator.userAgent.includes('MauvelyAssist/');

/** How long the page waits for the bridge before going on without it. */
const WAIT_MS = 3_000;

let arriving: Promise<Channel | null> | null = null;

function channel(): Promise<Channel | null> {
  if (!inDesktop()) return Promise.resolve(null);
  if (window.assistDesktop) return Promise.resolve(window.assistDesktop);
  arriving ??= new Promise((resolve) => {
    const arrived = () => {
      clearTimeout(timer);
      window.removeEventListener('assistdesktop', arrived);
      arriving = null;
      resolve(window.assistDesktop ?? null);
    };
    const timer = setTimeout(arrived, WAIT_MS);
    window.addEventListener('assistdesktop', arrived);
  });
  return arriving;
}

function parsed<T>(json: string, fallback: T): T {
  try {
    return JSON.parse(json) as T;
  } catch {
    return fallback;
  }
}

/** The machine's tools for a turn; undefined on the web. */
export async function desktopTools(): Promise<ClientTool[] | undefined> {
  const ch = await channel();
  if (!ch) return undefined;
  return new Promise((resolve) => ch.tools((json) => resolve(parsed<ClientTool[]>(json, []))));
}

/**
 * This PC, for Dispatch: null on the web, in a panel, and in an app from
 * before it ran Dispatch, none of which has `device()`. Remembered, so a
 * thread can be told from this PC's own.
 */
export async function desktopDevice(): Promise<DeviceInfo | null> {
  const ch = await channel();
  if (!ch?.device) return (ownId = null);
  const json = await new Promise<string>((resolve) => {
    const timer = setTimeout(() => resolve(''), WAIT_MS);
    ch.device!((j) => {
      clearTimeout(timer);
      resolve(j);
    });
  });
  const info = parseDevice(json);
  ownId = info?.id ?? null;
  return info;
}

/** The local MCP servers; null on the web. */
export async function desktopServers(): Promise<DesktopServer[] | null> {
  const ch = await channel();
  if (!ch) return null;
  return new Promise((resolve) => ch.servers((json) => resolve(parsed<DesktopServer[]>(json, []))));
}

export async function openDesktopSettings(section: 'folders' | 'servers' | 'computer'): Promise<void> {
  (await channel())?.openSettings(section);
}

/** Called when a folder or server changes; returns the unsubscribe. */
export function onDesktopChange(fn: () => void): () => void {
  let ch: Channel | null = null;
  let gone = false;
  void channel().then((c) => {
    if (gone || !c) return;
    ch = c;
    ch.toolsChanged.connect(fn);
  });
  return () => {
    gone = true;
    ch?.toolsChanged.disconnect(fn);
  };
}

/** Called when a sheet asking about a call opens (`on`) or is answered, with
 *  the id the call was run under; returns the unsubscribe. */
export function onDesktopAsking(fn: (runId: string, on: boolean) => void): () => void {
  let ch: Channel | null = null;
  let gone = false;
  void channel().then((c) => {
    if (gone || !c?.asking) return;
    ch = c;
    ch.asking!.connect(fn);
  });
  return () => {
    gone = true;
    ch?.asking?.disconnect(fn);
  };
}

/** Called with each request the shell makes for a sheet a paired phone may
 *  answer, as JSON; returns the unsubscribe. */
export function onDesktopApprovalRequest(fn: (json: string) => void): () => void {
  let ch: Channel | null = null;
  let gone = false;
  void channel().then((c) => {
    if (gone || !c?.approvalRequest) return;
    ch = c;
    ch.approvalRequest!.connect(fn);
  });
  return () => {
    gone = true;
    ch?.approvalRequest?.disconnect(fn);
  };
}

/** What the shell made of what a phone sent: taken, or refused and why. */
export interface ShellVerdict {
  ok: boolean;
  error?: string;
}

/** How long the page waits for the shell to check what a phone sent. */
const VERDICT_MS = 5_000;

/** The shell's `{"ok": …, "error"?: …}`; null for anything else. */
export function parseVerdict(json: string): ShellVerdict | null {
  const v = json ? parsed<{ ok?: unknown; error?: unknown } | null>(json, null) : null;
  if (!v || typeof v !== 'object' || typeof v.ok !== 'boolean') return null;
  return typeof v.error === 'string' ? { ok: v.ok, error: v.error } : { ok: v.ok };
}

/** Hand the shell what a phone sent, to check; null where the shell has no
 *  such member, or did not answer. The page only delivers. */
async function handOver(member: 'approve' | 'pair', json: string): Promise<ShellVerdict | null> {
  const ch = await channel();
  const take = ch?.[member];
  if (!ch || !take) return null;
  const answer = await new Promise<string>((resolve) => {
    const timer = setTimeout(() => resolve(''), VERDICT_MS);
    take.call(ch, json, (a) => {
      clearTimeout(timer);
      resolve(a);
    });
  });
  return parseVerdict(answer);
}

/** A paired phone's answer, as the hub's `approval` frame carried it, to the
 *  shell's `approve`: the frame without `t`, `chatId` and `messageId`. */
export const deliverApproval = ({ nonce, phone, decision, signature }: Extract<HubServerFrame, { t: 'approval' }>): Promise<ShellVerdict | null> =>
  handOver('approve', JSON.stringify({ nonce, phone, decision, signature }));

/** A phone asking to be paired, as the hub's `pair` frame carried it, to the
 *  shell's `pair`: the frame without `t`. */
export const deliverPairing = ({ phone, name, key, expires, mac, sig }: Extract<HubServerFrame, { t: 'pair' }>): Promise<ShellVerdict | null> =>
  handOver('pair', JSON.stringify({ phone, name, key, expires, mac, sig }));

/**
 * The images `finished` sent with a result: JSON of `{mime, data}`, the data
 * base64. The first four are read; each must be a PNG, JPEG, WebP or GIF of
 * at most 2 MB, and anything else is left out.
 */
export function resultImages(json: string | undefined): ResultImage[] {
  const list = json ? parsed<unknown>(json, null) : null;
  if (!Array.isArray(list)) return [];
  return list.slice(0, MAX_RESULT_IMAGES).flatMap((item): ResultImage[] => {
    const { mime, data } = (item ?? {}) as { mime?: unknown; data?: unknown };
    if (typeof mime !== 'string' || !RESULT_EXTENSIONS[mime] || typeof data !== 'string') return [];
    // Four characters for every three bytes: longer cannot fit.
    if (!data || data.length > Math.ceil(MAX_RESULT_IMAGE_BYTES / 3) * 4) return [];
    try {
      const bytes = Uint8Array.from(atob(data), (c) => c.charCodeAt(0));
      return bytes.length <= MAX_RESULT_IMAGE_BYTES ? [{ mime, bytes }] : [];
    } catch {
      return [];
    }
  });
}

function runOne(ch: Channel, call: PendingCall, messageId: string): Promise<DesktopResult & { images: ResultImage[] }> {
  // The app knows a call by this id; the Worker by the model's, which a
  // model can use again in its next reply.
  const runId = `${messageId}:${call.id}`;
  return new Promise((resolve) => {
    const finished = (callId: string, content: string, isError: boolean, images?: string) => {
      if (callId !== runId) return;
      ch.finished.disconnect(finished);
      resolve({ callId: call.id, content, ...(isError ? { isError: true } : {}), images: resultImages(images) });
    };
    ch.finished.connect(finished);
    ch.run(runId, call.name, call.arguments);
  });
}

/**
 * The calls a waiting reply left for the machine. The app answers a call it
 * has run before with the same result, so a reload never runs one twice.
 */
export const desktopCalls = (pending: readonly PendingCall[]): PendingCall[] =>
  pending.filter((c) => c.where === 'desktop' && c.needs === 'result');

/** A call's images as the chat's files, named after the tool. One that does
 *  not upload is left out: the call's text still goes back. */
async function uploaded(images: readonly ResultImage[], tool: string, chatId: string): Promise<string[]> {
  const base = tool.replace(/^(local|mcp)__(?:[a-z0-9-]+__)?/, '');
  const ids: string[] = [];
  for (const [i, image] of images.entries()) {
    const name = `${base}${images.length > 1 ? `-${i + 1}` : ''}.${RESULT_EXTENSIONS[image.mime]}`;
    const info = await uploadFile(new File([image.bytes], name, { type: image.mime }), { chatId }).catch(() => null);
    if (info) ids.push(info.id);
  }
  return ids;
}

/** The files each run's images became, by run id. The app replays a run
 *  without its images, so a take-on sent again goes on with these. */
const runFiles = new Map<string, string[]>();

/** Runs calls on the machine, one after another, and uploads the images each
 *  gave back to the chat; none on the web. A `signal` that aborts lets the
 *  call in hand finish and starts no other. `bind`: this page started the
 *  reply, so its windows are its chat's (`replyChat`); any other reply's are
 *  its own. */
export async function runOnDesktop(
  calls: readonly PendingCall[],
  messageId: string,
  chatId: string,
  signal?: AbortSignal,
  bind = false
): Promise<DesktopResult[]> {
  const ch = await channel();
  if (!ch) return [];
  // A reply this page started: the windows its calls are granted are the
  // chat's, for its next reply too. One from a wake the app hears of first;
  // any other reply is bound to nothing.
  if (calls.length) {
    if (fromWake.has(messageId)) ch.dispatched?.(messageId);
    else if (bind) ch.replyChat?.(messageId, chatId);
  }
  const results: DesktopResult[] = [];
  for (const call of calls) {
    if (signal?.aborted) break;
    const { images, ...result } = await runOne(ch, call, messageId);
    const runId = `${messageId}:${call.id}`;
    const files = images.length ? await uploaded(images, call.name, chatId) : (runFiles.get(runId) ?? []);
    if (files.length) runFiles.set(runId, files);
    results.push(files.length ? { ...result, files } : result);
  }
  return results;
}

/** How long a turn waits for a panel's app to say what it has open. */
const CONTEXT_MS = 1_500;
let contexts = 0;

/**
 * What a panel's app has open, for a turn: its `mcp__<app>__get_context`, run
 * like any other call. Null on the web; the text is empty when the app does
 * not offer it (Snap), or when it failed or took longer than 1.5 s.
 */
export async function hostContext(app: string, tools: readonly ClientTool[] | undefined): Promise<HostContext | null> {
  const name = `mcp__${app}__get_context`;
  if (!tools) return null;
  const ch = await channel();
  if (!ch) return null;
  if (!tools.some((t) => t.name === name)) return { app, text: '' };
  const call: PendingCall = { id: 'get_context', name, arguments: '{}', label: '', where: 'desktop', needs: 'result' };
  let timer: ReturnType<typeof setTimeout> | undefined;
  const late = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), CONTEXT_MS);
  });
  // An id of its own each time: the app answers one it has seen before with
  // what it answered then.
  const result = await Promise.race([runOne(ch, call, `context-${Date.now()}-${++contexts}`), late]);
  clearTimeout(timer);
  return { app, text: result && !result.isError ? result.content.trim().slice(0, MAX_HOST_CHARS) : '' };
}
